import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Slider } from '../../Common/slider';
// Importer les types nécessaires
import { Qualification, QualificationCreate, QualificationBase } from '../../../types'; // Utiliser QualificationBase pour update

// Interface de props mise à jour
interface QualificationFormProps {
  useCaseId: string;
  initialData?: Qualification | null;
  onSubmit: (data: QualificationCreate | QualificationBase) => Promise<void>;
  isEditing?: boolean;
  isLoading?: boolean;
  onCancelEdit?: () => void;
  submitLabel?: string;
}

const QualificationForm: React.FC<QualificationFormProps> = ({
  initialData,
  onSubmit,
  isEditing = false,
  isLoading = false,
  onCancelEdit,
  submitLabel,
}) => {
  const { t } = useTranslation();
  // États locaux pour les champs du formulaire
  const [valueScore, setValueScore] = useState<number>(5);
  const [complexityScore, setComplexityScore] = useState<number>(5);
  const [moscow, setMoscow] = useState<string>('');
  const [riceReach, setRiceReach] = useState<number>(50);
  const [riceImpact, setRiceImpact] = useState<number>(1);
  const [riceConfidence, setRiceConfidence] = useState<number>(50);
  const [riceEffort, setRiceEffort] = useState<number>(5);
  const [comment, setComment] = useState('');
  const [editingQualificationId, setEditingQualificationId] = useState<string | null>(null); // Pour savoir si on édite
  // Review fields
  const [needsReview, setNeedsReview] = useState<boolean>(false);
  const [reviewType, setReviewType] = useState<string>('');
  const [reviewOtherName, setReviewOtherName] = useState<string>('');

  // Fonction pour peupler le formulaire (accepte undefined)
  const populateForm = useCallback((data: Qualification | null | undefined) => {
      setValueScore(data?.value_score ?? 5);
      setComplexityScore(data?.complexity_score ?? 5);
      setMoscow(data?.moscow ?? '');
      setRiceReach(data?.rice_reach ?? 50);
      setRiceImpact(data?.rice_impact ?? 1);
      setRiceConfidence(data?.rice_confidence ?? 50);
      setRiceEffort(data?.rice_effort ?? 5);
      setComment(data?.notes ?? ''); // Utiliser 'notes' du type Qualification
      setEditingQualificationId(data?.id ?? null); // Mémoriser l'ID si on édite
      // Review fields
      setNeedsReview(data?.needs_review ?? false);
      setReviewType(data?.review_type ?? '');
      setReviewOtherName(data?.review_other_name ?? '');
  }, []); // Pas de dépendances, car elle utilise les setters

  useEffect(() => {
    populateForm(initialData);
  }, [initialData, populateForm]);

  const handleSubmit = async (e: React.FormEvent) => { // Rendre async
    e.preventDefault();
    // Construire le payload basé sur les états locaux
    // Utiliser QualificationCreate car c'est ce que la fonction onSubmit attend pour l'instant
    const payload = {
      value_score: valueScore,
      complexity_score: complexityScore,
      moscow: moscow || null,
      rice_reach: riceReach,
      rice_impact: riceImpact,
      rice_confidence: riceConfidence,
      rice_effort: riceEffort,
      notes: comment || null, // Utiliser 'notes' pour correspondre à QualificationBase/Create
      needs_review: needsReview,
      review_type: needsReview ? (reviewType || null) : null,
      review_other_name: needsReview && reviewType === 'Other' ? (reviewOtherName || null) : null,
    };
    try {
        await onSubmit(payload);
    } catch (err) {
        console.error("QualificationForm: Error during submission", err);
    }
  };

  // Bouton pour réinitialiser/vider le formulaire (utile si on édite depuis l'historique)
  const handleClearForm = () => {
      populateForm(null);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 border border-border rounded-xl bg-card">
      <h3 className="text-lg font-medium text-foreground mb-2">
        {editingQualificationId ? t('qualification.form.editTitle', { id: editingQualificationId.substring(0,6) }) : t('qualification.form.newTitle')}
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
          {t('qualification.form.helpText')}
      </p>

      {/* Champs du formulaire */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="valueScore" className="text-sm font-medium text-foreground">{t('qualification.form.valueScoreLabel')}</label>
          <span className="text-sm font-semibold text-primary tabular-nums w-6 text-right">{valueScore}</span>
        </div>
        <Slider
          id="valueScore"
          min={0}
          max={10}
          step={1}
          value={[valueScore]}
          onValueChange={([v]) => setValueScore(v)}
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs text-muted-foreground">0</span>
          <span className="text-xs text-muted-foreground">10</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="complexityScore" className="text-sm font-medium text-foreground">{t('qualification.form.complexityScoreLabel')}</label>
          <span className="text-sm font-semibold text-primary tabular-nums w-6 text-right">{complexityScore}</span>
        </div>
        <Slider
          id="complexityScore"
          min={0}
          max={10}
          step={1}
          value={[complexityScore]}
          onValueChange={([v]) => setComplexityScore(v)}
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs text-muted-foreground">0</span>
          <span className="text-xs text-muted-foreground">10</span>
        </div>
      </div>

       <div>
         <label htmlFor="moscow" className="block text-sm font-medium text-foreground">{t('qualification.form.moscowLabel')}</label>
         <select
           id="moscow"
           value={moscow}
           onChange={(e) => setMoscow(e.target.value)}
           className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-ring focus:ring-ring sm:text-sm"
         >
           <option value="">{t('qualification.form.moscowPlaceholder')}</option>
           <option value="MUST">{t('qualification.form.moscow.MUST')}</option>
           <option value="SHOULD">{t('qualification.form.moscow.SHOULD')}</option>
           <option value="COULD">{t('qualification.form.moscow.COULD')}</option>
           <option value="WONT">{t('qualification.form.moscow.WONT')}</option>
         </select>
       </div>

        <div className="space-y-4">
            <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="riceReach" className="text-sm font-medium text-foreground">{t('qualification.form.riceReachLabel')}</label>
                  <span className="text-sm font-semibold text-primary tabular-nums">{riceReach}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{t('qualification.rice.reachDesc')}</p>
                <Slider
                  id="riceReach"
                  min={0}
                  max={100}
                  step={1}
                  value={[riceReach]}
                  onValueChange={([v]) => setRiceReach(v)}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">0</span>
                  <span className="text-xs text-muted-foreground">100</span>
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="riceImpact" className="text-sm font-medium text-foreground">{t('qualification.form.riceImpactLabel')}</label>
                  <span className="text-sm font-semibold text-primary tabular-nums">{riceImpact}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{t('qualification.rice.impactDesc')}</p>
                <Slider
                  id="riceImpact"
                  min={0}
                  max={3}
                  step={1}
                  value={[riceImpact]}
                  onValueChange={([v]) => setRiceImpact(v)}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">0</span>
                  <span className="text-xs text-muted-foreground">3</span>
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="riceConfidence" className="text-sm font-medium text-foreground">{t('qualification.form.riceConfidenceLabel')}</label>
                  <span className="text-sm font-semibold text-primary tabular-nums">{riceConfidence}%</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{t('qualification.rice.confidenceDesc')}</p>
                <Slider
                  id="riceConfidence"
                  min={0}
                  max={100}
                  step={5}
                  value={[riceConfidence]}
                  onValueChange={([v]) => setRiceConfidence(v)}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">0%</span>
                  <span className="text-xs text-muted-foreground">100%</span>
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="riceEffort" className="text-sm font-medium text-foreground">{t('qualification.form.riceEffortLabel')}</label>
                  <span className="text-sm font-semibold text-primary tabular-nums">{riceEffort}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{t('qualification.rice.effortDesc')}</p>
                <Slider
                  id="riceEffort"
                  min={0}
                  max={20}
                  step={1}
                  value={[riceEffort]}
                  onValueChange={([v]) => setRiceEffort(v)}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-muted-foreground">0</span>
                  <span className="text-xs text-muted-foreground">20</span>
                </div>
            </div>
        </div>


      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-foreground">{t('global.notes')}</label>
        <textarea
          id="notes" // Changer l'ID pour correspondre
          value={comment} // L'état local s'appelle toujours comment
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-ring focus:ring-ring sm:text-sm"
        />
      </div>

      {/* Review Section - only in form (not in read-only view) */}
      <div className="border-t border-border pt-4 mt-4">
        <h4 className="text-base font-medium text-foreground mb-1">{t('qualification.form.reviewSection')}</h4>
        <p className="text-xs text-muted-foreground mb-3">{t('qualification.form.reviewSectionHelp')}</p>

        <div className="flex items-start gap-2 mb-3">
          <input
            type="checkbox"
            id="needsReview"
            checked={needsReview}
            onChange={(e) => setNeedsReview(e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring mt-0.5"
          />
          <div>
            <label htmlFor="needsReview" className="text-sm font-medium text-foreground">
              {t('qualification.form.needsReviewLabel')}
            </label>
            <p className="text-xs text-muted-foreground mt-0.5">{t('qualification.form.needsReviewDesc')}</p>
          </div>
        </div>

        {needsReview && (
          <div className="space-y-3 pl-6 border-l-2 border-muted">
            <div>
              <label htmlFor="reviewType" className="block text-sm font-medium text-foreground">{t('qualification.form.reviewTypeLabel')}</label>
              <select
                id="reviewType"
                value={reviewType}
                onChange={(e) => setReviewType(e.target.value)}
                className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-ring focus:ring-ring sm:text-sm"
              >
                <option value="">{t('qualification.form.reviewTypePlaceholder')}</option>
                <option value="Legal">{t('qualification.form.reviewTypeLegal')}</option>
                <option value="Tech">{t('qualification.form.reviewTypeTech')}</option>
                <option value="Other">{t('qualification.form.reviewTypeOther')}</option>
              </select>
              <div className="mt-1.5 text-xs text-muted-foreground space-y-0.5">
                <p><strong>{t('qualification.form.reviewTypeLegal')}:</strong> {t('qualification.form.reviewTypeLegalDesc')}</p>
                <p><strong>{t('qualification.form.reviewTypeTech')}:</strong> {t('qualification.form.reviewTypeTechDesc')}</p>
                <p><strong>{t('qualification.form.reviewTypeOther')}:</strong> {t('qualification.form.reviewTypeOtherDesc')}</p>
              </div>
            </div>

            {reviewType === 'Other' && (
              <div>
                <label htmlFor="reviewOtherName" className="block text-sm font-medium text-foreground">{t('qualification.form.reviewOtherNameLabel')}</label>
                <input
                  type="text"
                  id="reviewOtherName"
                  value={reviewOtherName}
                  onChange={(e) => setReviewOtherName(e.target.value)}
                  placeholder={t('qualification.form.reviewOtherNamePlaceholder')}
                  className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-ring focus:ring-ring sm:text-sm"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Afficher les erreurs */}
      {/* L'affichage de l'erreur est géré dans le composant parent (Tab) */}

      <div className="flex justify-end space-x-2">
         {/* Bouton pour vider le formulaire */}
         {editingQualificationId && (
             <button
               type="button"
               onClick={handleClearForm}
               disabled={isLoading}
               className="inline-flex justify-center rounded-md border border-input bg-background py-2 px-4 text-sm font-medium text-foreground shadow-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
             >
               {t('qualification.form.newQualification')}
             </button>
         )}
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex justify-center rounded-md border border-transparent bg-primary py-2 px-4 text-sm font-medium text-background shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
        >
          {isLoading ? t('global.saving') : (submitLabel ?? (isEditing ? t('global.update') : t('global.add')))}
        </button>
        {(isEditing || submitLabel) && onCancelEdit && (
             <button
               type="button"
               onClick={onCancelEdit}
               disabled={isLoading}
               className="inline-flex justify-center rounded-md border border-input bg-background py-2 px-4 text-sm font-medium text-foreground shadow-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
             >
               {t('global.cancel')}
             </button>
         )}
      </div>
    </form>
  );
};

// Ajouter l'exportation par défaut
export default QualificationForm;
